<template>
  <section>
    <section-title title="Búsqueda de Usuario" />
    <div class="container-fluid">
      <validation-observer ref="userFormValidator">
        <div class="row mb-5">
          <div class="col-md-4 col-sm-12">
            <validation-observer ref="validator">
              <validation-provider
                v-slot="{ errors }"
                :custom-messages="{
                  required: 'Favor seleccionar un usuario.',
                }"
                rules="required"
              >
                <CustomAutoComplete
                  id="user-search"
                  ref="autocompleteUserRef"
                  v-model="model.currentUserName"
                  :debounce-time="2000"
                  display-text-name="fullName"
                  :error="errors[0]"
                  :get-result-value="getResultValue"
                  label="Usuario:"
                  placeholder="Escriba un nombre o correo electrónico"
                  :search="searchHandler"
                  v-on="$listeners"
                  @submit="handleSubmit"
                />
              </validation-provider>
            </validation-observer>
          </div>
        </div>
        <div v-if="showSpecialistCreation">
          <hr />
          <div class="row">
            <div class="col-sm">
              <validation-provider v-slot="{ errors }" rules="required">
                <custom-input
                  v-model="specialistData.firstName"
                  :custom-error-msg="errorMsgs.firstName"
                  :error="errors[0]"
                  label="Nombre:"
                />
              </validation-provider>
            </div>
            <div class="col-sm">
              <custom-input
                v-model="specialistData.middleName"
                label="Segundo Nombre:"
              />
            </div>
            <div class="col-sm">
              <validation-provider v-slot="{ errors }" rules="required">
                <custom-input
                  v-model="specialistData.lastPaternalName"
                  :custom-error-msg="errorMsgs.lastPaternalName"
                  :error="errors[0]"
                  label="Apellido Paterno:"
                />
              </validation-provider>
            </div>
            <div class="col-sm">
              <custom-input
                v-model="specialistData.lastMaternalName"
                label="Apellido Materno:"
              />
            </div>
            <div class="col-sm">
              <validation-provider v-slot="{ errors }" rules="required">
                <custom-input
                  v-model="specialistData.email"
                  :custom-error-msg="errorMsgs.email"
                  :error="errors[0]"
                  label="Correo Electrónico:"
                />
              </validation-provider>
            </div>
          </div>
        </div>
      </validation-observer>
    </div>
  </section>
</template>

<script>
import SectionTitle from '@/components/common/page/SectionTitle';
import CustomAutoComplete from '@/components/common/CustomAutoComplete';
import CustomInput from '@/components/common/form/CustomInput';
import { searchUsers } from '@/modules/shared/provider.api';
import { getSpecialistByUser } from '@/modules/provider/api';
import { mapState } from 'vuex';
import alert from '@/mixins/alert.mixin';

const initialModel = () => ({
  currentUserName: null,
  usersOptions: [],
  searchInput: null,
  userSelected: null,
});

export default {
  name: '',
  components: {
    SectionTitle,
    CustomInput,
    CustomAutoComplete,
  },
  mixins: [alert],
  props: {
    csseFormValidator: {
      type: Object,
      default: () => {},
    },
  },
  data() {
    return {
      specialistData: {
        id: null,
        socialSecurityNumber: null,
        firstName: null,
        middleName: null,
        lastPaternalName: null,
        lastMaternalName: null,
        email: null,
        staffId: null,
        isRelatedToMaster: null,
      },
      model: initialModel(),
      searchResult: null,
      isSsnValid: true,
      errorMsgs: {
        firstName: 'Favor ingresar el nombre. (0115)',
        lastPaternalName: 'Favor ingresar el apellido paterno. (0118)',
        email: 'El campo Correo Electrónico es requerido. (0518)',
      },
    };
  },
  computed: {
    ...mapState('$_user', ['user']),
    page() {
      return {
        title: 'Información de Especialista',
        icon: 'fas fa-user-tie',
        routes: ['Proveedor', 'Especialistas'],
      };
    },
    availableActions() {
      return [
        {
          component: 'ActionButton',
          variant: 'success',
          text: 'Guardar',
          icon: 'fas fa-save',
          click: this.save,
        },
      ];
    },
    showSpecialistCreation() {
      return !!this.searchResult && !this.searchResult.id;
    },
    companyId() {
      return parseInt(this.user.profile.companyid);
    },
  },
  watch: {
    'model.userSelected': {
      handler: function(newVal) {
        if (newVal) {
          this.model.currentUserName = newVal.fullName;
        } else {
          this.model.currentUserName = null;
        }
      },
      deep: true,
    },
  },
  created() {
    this.$emit('on-create', this.availableActions);
  },
  methods: {
    searchHandler(input) {
      return new Promise((resolve) => {
        if (input.length < 3) {
          return resolve([]);
        }
        if (input == this.model.searchInput) {
          return resolve(this.model.usersOptions);
        }

        this.model.searchInput = input;
        searchUsers({
          name: this.model.searchInput,
          pageSize: 500,
        }).then((resp) => {
          this.model.usersOptions = resp.data.data;
          resolve(this.model.usersOptions);
        });
      });
    },
    getResultValue(result) {
      return result.fullName;
    },
    handleSubmit(value) {
      this.model.userSelected = value;
    },
    async save() {
      var self = this;

      const userFormIsValid = await this.$refs['userFormValidator'].validate();
      const csseFormIsValid = await this.csseFormValidator.validate();

      if (userFormIsValid && csseFormIsValid) {
        this.specialistData = {
          ...this.specialistData,
          firstName: this.model.userSelected.firstName,
          middleName: this.model.userSelected.middleName,
          lastPaternalName: this.model.userSelected.lastPaternalName,
          lastMaternalName: this.model.userSelected.lastMaternalName,
          userId: this.model.userSelected.userId,
          email: this.model.userSelected.email,
          socialSecurityNumber: this.model.userSelected.socialSecurityNumber,
          staffId: this.model.userSelected.staffId,
        };

        self.specialistData.fullName = `${self.specialistData.firstName} ${self.specialistData.middleName} ${self.specialistData.lastPaternalName} ${self.specialistData.lastMaternalName}`.trim();

        try {
          const { data } = await getSpecialistByUser(
            this.model.userSelected.userId
          );

          if (data.id) {
            this.ShowToast(
              'Atención',
              'Se encontró que el especialista ya pertenece a su corporación.',
              'warning'
            );

            self.$emit('on-specialist-found', data);
            return;
          }

          self.$emit('on-specialist-added', self.specialistData);
        } catch (err) {
          this.ShowToast('Error', err.response.data.message, 'error');
        }
      } else {
        self.ShowToast(
          'Error',
          'Favor de completar la información solicitada.',
          'error'
        );
      }
    },
  },
};
</script>

<style scoped>
.h-btn {
  height: 40px;
}
</style>
